/**
 * VideoActionModal — пикер папки Диска отдела для «Транскрибировать»/«Сделать
 * постмит» по видео. Резолвит Диск отдела чата (save-target), даёт навигацию по
 * папкам, «Сохранить сюда» запускает фоновую задачу (результат прилетит
 * сообщением в чат). Портал в body, стили .vam-* в chat-redesign.css.
 */
import { useState } from "react";
import { createPortal } from "react-dom";
import { useQuery } from "@tanstack/react-query";

import { Icon } from "@/components/prototype/Icon";
import {
  fetchDriveList, getVideoSaveTarget, transcribeVideo, makeVideoPostmit,
} from "@/api/drive";

export function VideoActionModal({ attachmentId, action, onClose, onStarted }: {
  attachmentId: number;
  action: "transcribe" | "postmit";
  onClose: () => void;
  onStarted: (action: "transcribe" | "postmit") => void;
}) {
  const [folderId, setFolderId] = useState<number | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const targetQ = useQuery({
    queryKey: ["video-save-target", attachmentId],
    queryFn: () => getVideoSaveTarget(attachmentId),
    staleTime: 60_000,
  });
  const driveId = targetQ.data?.drive_topic_id;
  const listQ = useQuery({
    queryKey: ["drive-list", driveId, folderId],
    queryFn: () => fetchDriveList(driveId!, folderId),
    enabled: !!driveId,
  });
  const breadcrumbs = listQ.data?.breadcrumbs ?? [];
  const folders = listQ.data?.folders ?? [];

  const submit = async () => {
    if (!driveId) return;
    setSubmitting(true);
    try {
      if (action === "transcribe") await transcribeVideo(attachmentId, driveId, folderId);
      else await makeVideoPostmit(attachmentId, driveId, folderId);
      onStarted(action);
      onClose();
    } catch (e) {
      setSubmitting(false);
      alert("Не удалось запустить: " + String((e as Error)?.message || e).slice(0, 140));
    }
  };

  const title = action === "transcribe" ? "🎬 Транскрибировать видео" : "📝 Сделать постмит";

  return createPortal(
    <div className="vam-overlay" onClick={onClose}>
      <div className="vam-modal" onClick={(e) => e.stopPropagation()}>
        <div className="vam-head">
          <b>{title}</b>
          <button type="button" className="vam-close" onClick={onClose} aria-label="Закрыть">
            <Icon name="x" size={16}/>
          </button>
        </div>
        <div className="vam-sub">
          Сохранить в: <b>{targetQ.data?.drive_name ?? "…"}</b>
          {breadcrumbs.length > 0 && (
            <span className="vam-path"> / {breadcrumbs.map((b) => b.name).join(" / ")}</span>
          )}
        </div>
        <div className="vam-list">
          {folderId != null && (
            <button
              type="button" className="vam-row vam-up"
              onClick={() => setFolderId(breadcrumbs.length >= 2 ? breadcrumbs[breadcrumbs.length - 2].id : null)}
            >
              <Icon name="chevron_l" size={15}/> Назад
            </button>
          )}
          {listQ.isLoading && <div className="vam-empty">Загрузка папок…</div>}
          {folders.map((f) => (
            <button key={f.id} type="button" className="vam-row vam-folder" onClick={() => setFolderId(f.id)}>
              <Icon name="drag" size={16}/> <span>{f.name}</span>
              <span className="vam-arrow">›</span>
            </button>
          ))}
          {!listQ.isLoading && folders.length === 0 && (
            <div className="vam-empty">Здесь нет подпапок — сохранится в эту папку</div>
          )}
        </div>
        <div className="vam-foot">
          <button type="button" className="vam-btn vam-cancel" onClick={onClose}>Отмена</button>
          <button type="button" className="vam-btn vam-save" disabled={!driveId || submitting} onClick={submit}>
            {submitting ? "Запуск…" : "Сохранить сюда"}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
